import React, { useState, useEffect } from 'react';

const SystemAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };
  const authHeadersForForm = { 'Authorization': `Bearer ${adminToken}` };

  // Backup & Restore System states
  const [backups, setBackups] = useState([]);
  const [backupNote, setBackupNote] = useState('');
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [restoreUrl, setRestoreUrl] = useState('');
  const [uploadingBackup, setUploadingBackup] = useState(false);
  const [showRestoreConfirmModal, setShowRestoreConfirmModal] = useState(false);
  const [restoreAction, setRestoreAction] = useState(null);
  const [showDeleteBackupModal, setShowDeleteBackupModal] = useState(false);
  const [backupToDelete, setBackupToDelete] = useState(null);
  const [backupSchedule, setBackupSchedule] = useState({
    enabled: false,
    frequency: 'weekly',
    time_hour: 3,
    day_of_week: 0,
    day_of_month: 1,
    max_backups: 5,
    next_run: null,
    last_run: null,
    last_status: null
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [backupStorage, setBackupStorage] = useState(null);
  const [downloadingBackupId, setDownloadingBackupId] = useState(null);
  const [maxBackups, setMaxBackups] = useState(30);
  const [showForceCacheClearModal, setShowForceCacheClearModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // BACKUP SCHEDULE FUNCTIONS
  const loadBackupSchedule = async () => {
    try {
      const response = await fetch(`${API}/admin/backup/schedule`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setBackupSchedule(data);
      }
    } catch (error) {
      console.error('Error loading backup schedule:', error);
    }
  };

  const saveBackupSchedule = async () => {
    setSavingSchedule(true);
    try {
      const response = await fetch(`${API}/admin/backup/schedule`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(backupSchedule)
      });
      if (response.ok) {
        const data = await response.json();
        showNotification(data.message || 'Schedule saved', 'success');
        if (data.next_run) {
          setBackupSchedule(prev => ({ ...prev, next_run: data.next_run }));
        }
      } else {
        const err = await response.json().catch(() => ({}));
        console.error('[BACKUP SCHEDULE] Save failed:', response.status, err);
        showNotification(err.detail || 'Failed to save schedule', 'error');
      }
    } catch (error) {
      console.error('[BACKUP SCHEDULE] Error:', error);
      showNotification('Failed to save schedule: ' + error.message, 'error');
    } finally {
      setSavingSchedule(false);
    }
  };

  // BACKUP & RESTORE FUNCTIONS
  const loadBackups = async () => {
    try {
      const response = await fetch(`${API}/admin/backup/list`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
        if (data.max_backups) setMaxBackups(data.max_backups);
        if (data.storage) setBackupStorage(data.storage);
      }
    } catch (error) {
      console.error('Error loading backups:', error);
    }
  };

  const createBackup = async () => {
    setLoadingBackup(true);
    try {
      const response = await fetch(`${API}/admin/backup/create`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          note: backupNote.trim() || null
        })
      });

      const text = await response.text();
      console.log('[BACKUP] Raw response:', text);

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.log('[BACKUP] Could not parse JSON:', e);
        showNotification('Backup may have been created. Please refresh.', 'warning');
        loadBackups();
        return;
      }

      if (data.success || response.status === 200) {
        showNotification(`Backup created! Size: ${data.size_mb || '?'}MB`, 'success');
        setBackupNote('');
        loadBackups();
      } else {
        showNotification(data.detail || 'Failed to create backup', 'error');
      }
    } catch (error) {
      console.error('[BACKUP] Fetch error:', error);
      showNotification('Checking backup status...', 'warning');
      loadBackups();
    } finally {
      setLoadingBackup(false);
    }
  };

  const downloadBackup = async (backupId, createdAt) => {
    setDownloadingBackupId(backupId);
    try {
      // Use a hidden iframe + direct URL approach won't work (needs auth header)
      // Instead, use fetch but stream to disk via showSaveFilePicker when available,
      // fall back to blob for smaller files
      const response = await fetch(`${API}/admin/backup/download/${backupId}`, {
        headers: authHeaders
      });

      if (!response.ok) {
        showNotification('Failed to download backup', 'error');
        return;
      }

      const filename = `backup_${createdAt}.zip`;

      // Modern browsers: stream directly to disk (no RAM limit)
      if (window.showSaveFilePicker) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'ZIP Archive', accept: { 'application/zip': ['.zip'] } }]
          });
          const writable = await fileHandle.createWritable();
          const reader = response.body.getReader();
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            await writable.write(value);
          }
          await writable.close();
          showNotification('Backup downloaded successfully!', 'success');
          return;
        } catch (pickerErr) {
          // User cancelled the save dialog
          if (pickerErr.name === 'AbortError') return;
          // Fall through to blob method
        }
      }

      // Fallback: blob method (works everywhere, uses RAM)
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showNotification('Backup downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading backup:', error);
      showNotification('Failed to download backup', 'error');
    } finally {
      setDownloadingBackupId(null);
    }
  };

  const restoreBackup = async (backupId) => {
    setRestoreAction({ type: 'storage', backupId });
    setShowRestoreConfirmModal(true);
  };

  const confirmRestore = async () => {
    if (!restoreAction) return;

    setShowRestoreConfirmModal(false);
    setLoadingBackup(true);

    try {
      if (restoreAction.type === 'storage') {
        const response = await fetch(`${API}/admin/backup/restore/${restoreAction.backupId}`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ mode: 'replace' })
        });

        if (response.ok) {
          showNotification('Database restored successfully!', 'success');
          setTimeout(() => window.location.reload(), 2000);
        } else {
          showNotification('Failed to restore backup', 'error');
        }
      } else if (restoreAction.type === 'url') {
        const response = await fetch(`${API}/admin/backup/restore-from-url`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ backup_url: restoreUrl, mode: 'replace' })
        });

        if (response.ok) {
          showNotification('Backup downloaded and restored from URL!', 'success');
          setRestoreUrl('');
          setTimeout(() => window.location.reload(), 2000);
        } else {
          showNotification('Failed to restore from URL', 'error');
        }
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      showNotification('Failed to restore backup', 'error');
    } finally {
      setLoadingBackup(false);
      setRestoreAction(null);
    }
  };

  const deleteBackup = async (backup) => {
    setBackupToDelete(backup);
    setShowDeleteBackupModal(true);
  };

  const confirmDeleteBackup = async () => {
    if (!backupToDelete) return;

    setShowDeleteBackupModal(false);

    try {
      const response = await fetch(`${API}/admin/backup/${backupToDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('Backup deleted successfully!', 'success');
        loadBackups();
      } else {
        showNotification('Failed to delete backup', 'error');
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      showNotification('Failed to delete backup', 'error');
    }

    setBackupToDelete(null);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingBackup(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', 'replace');

      const response = await fetch(`${API}/admin/backup/upload-restore`, {
        method: 'POST',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification('Backup uploaded and restored successfully!', 'success');
        setTimeout(() => window.location.reload(), 2000);
      } else {
        showNotification('Failed to upload and restore backup', 'error');
      }
    } catch (error) {
      console.error('Error uploading backup:', error);
      showNotification('Failed to upload backup', 'error');
    } finally {
      setUploadingBackup(false);
    }
  };

  const restoreFromUrl = () => {
    if (!restoreUrl) {
      showNotification('Please enter a valid URL', 'error');
      return;
    }
    setRestoreAction({ type: 'url' });
    setShowRestoreConfirmModal(true);
  };

  const handleForceCacheClear = async () => {
    setShowForceCacheClearModal(false);

    try {
      setIsLoading(true);
      const response = await fetch(`${API}/admin/force-cache-clear`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        showNotification(`Cache clear forced! Version: ${data.newCacheVersion}. All users will clear cache on next load.`, "success");
      } else {
        throw new Error("Failed to force cache clear");
      }
    } catch (error) {
      console.error("Error forcing cache clear:", error);
      showNotification("Failed to force cache clear", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Load backups on mount
  useEffect(() => {
    loadBackups();
    loadBackupSchedule();
  }, []);

  return (
    <>
      <div className="space-y-6">
        <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🔧 System Backup
        </h3>

        {/* FULL WEBSITE BACKUP & RESTORE SYSTEM */}
        <div className={`p-8 rounded-2xl shadow-2xl border-2 ${
          isDarkMode
            ? 'bg-gradient-to-br from-blue-900/40 via-purple-900/40 to-indigo-900/40 border-blue-500/30'
            : 'bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 border-blue-300'
        }`}>
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-600' : 'bg-blue-500'}`}>
              <span className="text-3xl">💾</span>
            </div>
            <div>
              <h4 className={`text-3xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Full Website Backup & Restore
              </h4>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Like hosting providers - Complete website snapshot
              </p>
            </div>
          </div>

          {/* What's Included Badge */}
          <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/70'}`}>
            <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              📦 Full backup includes:
            </p>
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                🗄️ Database (all data)
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                ⚙️ Backend Code
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                🎨 Frontend Code
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'}`}>
                📁 Uploads (images, audio)
              </span>
            </div>
          </div>

          {/* Create Backup */}
          <div className="mb-6">
            <h5 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              📦 Create New Full Backup
            </h5>
            <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Create a complete website snapshot - database + code + uploads in one ZIP file. Perfect for disaster recovery or moving servers!
            </p>

            {/* Backup Note Input */}
            <div className="mb-3">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                📝 Backup Note (Optional)
              </label>
              <input
                type="text"
                value={backupNote}
                onChange={(e) => setBackupNote(e.target.value)}
                placeholder="e.g., Working version before changes, Fixed login bug, etc."
                className={`w-full px-4 py-2 rounded-lg border ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                maxLength={100}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Add a note to remember what was working (max 100 characters)
              </p>
            </div>

            <button
              onClick={createBackup}
              disabled={loadingBackup}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50"
            >
              {loadingBackup ? '⏳ Creating Full Backup...' : '💾 Create Full Website Backup'}
            </button>
          </div>

          <hr className={`my-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} />

          {/* Automatic Backup Schedule */}
          <div className="mb-6">
            <h5 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {backupSchedule.enabled ? '\u{23F0}' : '\u{1F570}\u{FE0F}'} Automatic Backup
            </h5>
            <div className={`p-5 rounded-xl border ${
              backupSchedule.enabled
                ? isDarkMode ? 'bg-green-900/20 border-green-500/30' : 'bg-green-50 border-green-300'
                : isDarkMode ? 'bg-gray-800/50 border-gray-600/30' : 'bg-gray-50 border-gray-200'
            }`}>
              {/* Enable toggle */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    Enable Automatic Backups
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Server will automatically create backups on schedule
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={backupSchedule.enabled}
                    onChange={(e) => setBackupSchedule(prev => ({ ...prev, enabled: e.target.checked }))}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
                </label>
              </div>

              {backupSchedule.enabled && (
                <div className="space-y-4">
                  {/* Frequency */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Frequency
                    </label>
                    <div className="flex gap-2">
                      {[
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'monthly', label: 'Monthly' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setBackupSchedule(prev => ({ ...prev, frequency: opt.value }))}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            backupSchedule.frequency === opt.value
                              ? 'bg-blue-600 text-white'
                              : isDarkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Day of week (for weekly) */}
                  {backupSchedule.frequency === 'weekly' && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Day of Week
                      </label>
                      <select
                        value={backupSchedule.day_of_week}
                        onChange={(e) => setBackupSchedule(prev => ({ ...prev, day_of_week: parseInt(e.target.value) }))}
                        className={`px-3 py-2 rounded-lg border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, i) => (
                          <option key={i} value={i}>{day}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Day of month (for monthly) */}
                  {backupSchedule.frequency === 'monthly' && (
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Day of Month
                      </label>
                      <select
                        value={backupSchedule.day_of_month}
                        onChange={(e) => setBackupSchedule(prev => ({ ...prev, day_of_month: parseInt(e.target.value) }))}
                        className={`px-3 py-2 rounded-lg border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Time */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Time (UTC)
                    </label>
                    <select
                      value={backupSchedule.time_hour}
                      onChange={(e) => setBackupSchedule(prev => ({ ...prev, time_hour: parseInt(e.target.value) }))}
                      className={`px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>

                  {/* Max backups to keep */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Keep Last (auto backups)
                    </label>
                    <select
                      value={backupSchedule.max_backups}
                      onChange={(e) => setBackupSchedule(prev => ({ ...prev, max_backups: parseInt(e.target.value) }))}
                      className={`px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {[3, 5, 7, 10, 15, 20, 30].map(n => (
                        <option key={n} value={n}>{n} backups</option>
                      ))}
                    </select>
                  </div>

                  {/* Status info */}
                  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Next backup: </span>
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {backupSchedule.next_run ? new Date(backupSchedule.next_run).toLocaleString() : 'Not scheduled'}
                        </span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Last backup: </span>
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {backupSchedule.last_run ? new Date(backupSchedule.last_run).toLocaleString() : 'Never'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save button */}
              <button
                onClick={saveBackupSchedule}
                disabled={savingSchedule}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
              >
                {savingSchedule ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </div>

          <hr className={`my-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} />

          {/* Restore Options */}
          <div className="mb-6">
            <h5 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              🔄 Restore Website
            </h5>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
              ⚠️ Warning: Restore will replace current website with backup version!
            </p>

            {/* Option 1: Upload from Computer */}
            <div className="mb-4">
              <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Option 1: Upload from Computer
              </p>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center ${isDarkMode ? 'border-gray-500 bg-gray-800' : 'border-gray-300 bg-gray-50'}`}>
                <input
                  type="file"
                  accept=".zip"
                  onChange={handleFileUpload}
                  disabled={uploadingBackup}
                  className="hidden"
                  id="backup-upload"
                />
                <label
                  htmlFor="backup-upload"
                  className={`cursor-pointer ${uploadingBackup ? 'opacity-50' : ''}`}
                >
                  <div className="text-4xl mb-2">📂</div>
                  <p className={`font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {uploadingBackup ? '⏳ Restoring Full Website...' : 'Click to upload backup ZIP'}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Full website backup .zip files
                  </p>
                </label>
              </div>
            </div>

            {/* Option 2: Restore from Cloud Link */}
            <div className="mb-4">
              <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Option 2: Restore from Cloud Link 🌐
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={restoreUrl}
                  onChange={(e) => setRestoreUrl(e.target.value)}
                  placeholder="Paste Dropbox/Google Drive/OneDrive link..."
                  className={`flex-1 px-4 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                <button
                  onClick={restoreFromUrl}
                  disabled={!restoreUrl || loadingBackup}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold disabled:opacity-50"
                >
                  🔄 Restore from Link
                </button>
              </div>
              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Supported: Dropbox, Google Drive, OneDrive, or any direct HTTP/HTTPS link
              </p>
            </div>
          </div>

          <hr className={`my-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} />

          {/* Storage Warning Banner */}
          {backupStorage && (
            <div className={`mb-4 p-3 rounded-lg flex items-center justify-between ${
              backupStorage.total_size_mb >= backupStorage.max_storage_mb
                ? 'bg-red-100 border border-red-300 text-red-800'
                : backupStorage.total_size_mb >= backupStorage.max_storage_mb * 0.8
                  ? 'bg-yellow-100 border border-yellow-300 text-yellow-800'
                  : isDarkMode ? 'bg-gray-800 border border-gray-600 text-gray-300' : 'bg-gray-50 border border-gray-200 text-gray-600'
            }`}>
              <div className="flex items-center gap-2">
                <span>{backupStorage.total_size_mb >= backupStorage.max_storage_mb ? '🔴' : backupStorage.total_size_mb >= backupStorage.max_storage_mb * 0.8 ? '🟡' : '🟢'}</span>
                <span className="text-sm font-medium">
                  Storage: {backupStorage.total_size_mb}MB / {backupStorage.max_storage_mb}MB
                  ({backupStorage.files_on_disk} files)
                </span>
              </div>
              {backupStorage.total_size_mb >= backupStorage.max_storage_mb * 0.8 && (
                <span className="text-xs font-semibold">
                  Download old backups to your desktop, then delete them to free space
                </span>
              )}
            </div>
          )}

          {/* Stored Backups List */}
          <div>
            <h5 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              📦 Stored Backups ({backups.length}/{maxBackups})
            </h5>

            {backups.length === 0 ? (
              <div className={`p-8 text-center rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                  No backups stored yet. Create your first backup above!
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {backups.map((backup) => (
                  <div
                    key={backup.id}
                    className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {backup.note ? (
                          <>
                            <h6 className={`font-bold text-base mb-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                              📝 {backup.note}
                            </h6>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              Backup_{new Date(backup.created_at).toLocaleDateString('en-US').replace(/\//g, '-')}_{new Date(backup.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }).replace(':', '-')}
                            </p>
                          </>
                        ) : (
                          <h6 className={`font-semibold text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            💾 Backup_{new Date(backup.created_at).toLocaleDateString('en-US').replace(/\//g, '-')}_{new Date(backup.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }).replace(':', '-')}
                          </h6>
                        )}
                        <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Created: {new Date(backup.created_at).toLocaleString()} • Size: {backup.size_mb}MB • {backup.total_records || backup.total_documents || 0} records {backup.total_files ? `• ${backup.total_files} files` : ''}
                        </p>
                        {/* Backup Type Badge */}
                        <div className="flex flex-wrap gap-1">
                          {backup.type === 'full_website' || backup.contents ? (
                            <>
                              <span className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                                🗄️ Database
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                                ⚙️ Backend
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                                🎨 Frontend
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800'}`}>
                                📁 Uploads
                              </span>
                            </>
                          ) : (
                            <span className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                              🗄️ Database Only (old format)
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-3 ml-4">
                        <button
                          onClick={() => downloadBackup(backup.id, new Date(backup.created_at).toISOString().replace(/[:.]/g, '-').slice(0, -5))}
                          className={`px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${downloadingBackupId === backup.id ? 'bg-blue-400 cursor-wait' : 'bg-blue-600 hover:bg-blue-700'}`}
                          title="Download"
                          disabled={downloadingBackupId === backup.id}
                        >
                          {downloadingBackupId === backup.id ? '⏳ Downloading...' : '📥 Download'}
                        </button>
                        <button
                          onClick={() => restoreBackup(backup.id)}
                          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                          title="Restore"
                        >
                          🔄 Restore
                        </button>
                        <button
                          onClick={() => deleteBackup(backup)}
                          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                          title="Delete"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                    <p className={`mt-3 text-xs ${isDarkMode ? 'text-emerald-400/80' : 'text-emerald-700'}`}>
                      ℹ️ Restore overwrites content. User accounts, signups, payments, and learning progress are preserved.
                    </p>
                  </div>
                ))}
              </div>
            )}

            {backups.length >= maxBackups && (
              <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                  ⚠️ Storage limit reached ({backups.length}/{maxBackups}). Oldest backups will be automatically deleted when creating new ones.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Force Clear All User Caches Card */}
        <div className={`p-8 rounded-2xl shadow-2xl border-2 ${
          isDarkMode
            ? 'bg-gradient-to-br from-orange-900/40 via-red-900/40 to-pink-900/40 border-orange-500/30'
            : 'bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 border-orange-300'
        }`}>
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-red-600' : 'bg-red-500'}`}>
              <span className="text-3xl">🔥</span>
            </div>
            <h4 className={`text-3xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Force Clear All User Caches
            </h4>
          </div>

          <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Force all users (including yourself) to automatically clear their browser cache and reload the app.
            This is useful when you deploy new features or fixes and want everyone to see them immediately.
          </p>

          <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-300'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
              ℹ️ <strong>How it works:</strong> This increments the cache version number in the database.
              When users next load the app, it will automatically clear their cache and reload with the latest version.
            </p>
          </div>

          <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
              ⚠️ <strong>Note:</strong> Users will see a brief reload - no manual action required on their part.
            </p>
          </div>

          <button
            onClick={() => setShowForceCacheClearModal(true)}
            disabled={isLoading}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold text-lg disabled:opacity-50"
          >
            🔥 FORCE CLEAR ALL USER CACHES
          </button>
        </div>

        {/* Clear Browser Cache Card */}
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'}`}>
          <h4 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            🗑️ Clear Browser Cache
          </h4>

          <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Clear all cached data including localStorage, sessionStorage, service workers, and cookies.
            Useful for testing and debugging.
          </p>

          <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
              ⚠️ <strong>Warning:</strong> This will log you out and reload the page.
            </p>
          </div>

          <button
            onClick={() => {
              const modal = document.createElement('div');
              modal.innerHTML = `
                <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 1rem;">
                  <div style="max-width: 28rem; width: 100%; padding: 1.5rem; border-radius: 1rem; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); background: ${isDarkMode ? '#1f2937' : 'white'};">
                    <div style="text-align: center;">
                      <div style="margin: 0 auto 1rem; display: flex; align-items: center; justify-content: center; height: 5rem; width: 5rem; border-radius: 9999px; background: #fee2e2;">
                        <span style="font-size: 3rem;">🗑️</span>
                      </div>
                      <h3 style="font-size: 1.5rem; font-weight: bold; margin-bottom: 0.75rem; color: ${isDarkMode ? 'white' : '#111827'};">
                        Clear All Browser Cache?
                      </h3>
                      <div style="margin-bottom: 1.5rem; color: ${isDarkMode ? '#d1d5db' : '#4b5563'};">
                        <p style="margin-bottom: 0.75rem;">This will clear all cached data:</p>
                        <div style="padding: 1rem; border-radius: 0.5rem; text-align: left; background: ${isDarkMode ? '#374151' : '#f3f4f6'};">
                          <ul style="list-style: none; padding: 0; font-size: 0.875rem;">
                            <li style="margin-bottom: 0.5rem;">✓ Local Storage</li>
                            <li style="margin-bottom: 0.5rem;">✓ Session Storage</li>
                            <li style="margin-bottom: 0.5rem;">✓ Browser Cookies</li>
                            <li>✓ Service Workers</li>
                          </ul>
                        </div>
                        <div style="margin-top: 1rem; padding: 0.75rem; border-radius: 0.5rem; background: ${isDarkMode ? 'rgba(146, 64, 14, 0.3)' : '#fef3c7'};">
                          <p style="font-size: 0.875rem; font-weight: 600; color: ${isDarkMode ? '#fcd34d' : '#92400e'};">
                            ⚠️ You will be logged out and the page will reload
                          </p>
                        </div>
                      </div>
                      <div style="display: flex; gap: 0.75rem;">
                        <button id="cancelBtn" style="flex: 1; padding: 0.75rem 1.5rem; border-radius: 0.5rem; font-weight: bold; cursor: pointer; border: none; background: ${isDarkMode ? '#374151' : '#e5e7eb'}; color: ${isDarkMode ? 'white' : '#1f2937'};">
                          Cancel
                        </button>
                        <button id="confirmBtn" style="flex: 1; padding: 0.75rem 1.5rem; background: linear-gradient(to right, #dc2626, #b91c1c); color: white; border-radius: 0.5rem; font-weight: bold; cursor: pointer; border: none;">
                          Yes, Clear Everything
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              `;
              document.body.appendChild(modal);

              modal.querySelector('#cancelBtn').onclick = () => {
                document.body.removeChild(modal);
              };

              modal.querySelector('#confirmBtn').onclick = async () => {
                try {
                  localStorage.clear();
                  sessionStorage.clear();
                  document.cookie.split(";").forEach(c => {
                    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                  });
                  if ('serviceWorker' in navigator) {
                    const registrations = await navigator.serviceWorker.getRegistrations();
                    for(let registration of registrations) {
                      await registration.unregister();
                    }
                  }
                  showNotification('Cache cleared! Reloading...', 'success');
                  setTimeout(() => window.location.reload(true), 1000);
                } catch (error) {
                  showNotification('Error clearing cache', 'error');
                  document.body.removeChild(modal);
                }
              };
            }}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold text-lg"
          >
            🗑️ CLEAR ALL CACHE & RELOAD
          </button>
        </div>

        {/* System Info Card */}
        <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'}`}>
          <h4 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            📊 System Information
          </h4>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Browser:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {navigator.userAgent.split('(')[1]?.split(')')[0] || 'Unknown'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>LocalStorage Items:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {Object.keys(localStorage).length}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Service Worker:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {'serviceWorker' in navigator ? 'Supported' : 'Not Supported'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Online Status:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {navigator.onLine ? '🟢 Online' : '🔴 Offline'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Force Cache Clear Confirmation Modal */}
      {showForceCacheClearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-4xl">🔥</span>
                </div>
              </div>
              <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Force Clear All User Caches?
              </h3>
              <p className={`text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                This will force <strong>ALL users</strong> to clear their cache and reload the app.
                They will see the latest version automatically on their next page load.
              </p>
            </div>

            <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-300'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                ℹ️ Users will experience a brief automatic reload - no manual action required from them.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowForceCacheClearModal(false)}
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                  isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleForceCacheClear}
                disabled={isLoading}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Yes, Clear All Caches'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Backup Confirmation Modal */}
      {showRestoreConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-4xl">⚠️</span>
                </div>
              </div>
              <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Restore Database?
              </h3>
              <p className={`text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                This will <strong>REPLACE ALL current data</strong> with the backup.
                This action cannot be undone!
              </p>
            </div>

            <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                ⚠️ <strong>Warning:</strong> All current users, translations, orders, and settings will be replaced.
                The page will automatically reload after restore.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRestoreConfirmModal(false);
                  setRestoreAction(null);
                }}
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                  isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmRestore}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold"
              >
                Yes, Restore Database
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Backup Confirmation Modal */}
      {showDeleteBackupModal && backupToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-md w-full shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-4xl">🗑️</span>
                </div>
              </div>
              <h3 className={`text-xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Delete Backup?
              </h3>
              <p className={`text-center mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to permanently delete this backup?
              </p>

              {/* Backup Details */}
              <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                {backupToDelete.note ? (
                  <p className={`font-semibold mb-1 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    📝 {backupToDelete.note}
                  </p>
                ) : (
                  <p className={`font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    📦 {backupToDelete.filename || 'Backup'}
                  </p>
                )}
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Created: {new Date(backupToDelete.created_at).toLocaleString()}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Size: {backupToDelete.size_mb}MB • {backupToDelete.total_records || backupToDelete.total_documents || 0} records
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg mb-6 ${isDarkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50 border border-red-300'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                ⚠️ <strong>Warning:</strong> This action cannot be undone. The backup ZIP file will be permanently deleted from the server.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteBackupModal(false);
                  setBackupToDelete(null);
                }}
                className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                  isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteBackup}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold"
              >
                Yes, Delete Backup
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SystemAdmin;
